import Service from '@ember/service';
import { service } from '@ember/service';
import cloneDeep from 'lodash.clonedeep';
import { waitForPromise } from '@ember/test-waiters';

import LtSpiceData from '../data/ltspicedata';
import Consts from '../utils/constants';
import LtSpiceConsts from '../utils/lt-spice';
import PrettyNum from '../utils/pretty-num-wrapper';
import utils from '../utils/utils';

export default class ltSpiceService extends Service {
    @service('data') model;
    @service intl;
    @service selectionData;
    @service urlData;

    generateLtSpiceData() {
        // This is where devices parameters are gathered for LTspice schematic generation
        let intl = this.intl;
        let vdiff = PrettyNum.spice(this.model.vref / (this.model.gain * 2));
        let frequency = PrettyNum.spice(this.model.frequency);
        let topology = this.selectionData.essentiallyFollower
            ? this.selectionData.findOneOptionById('topology', Consts.TOPOLOGY.FOLLOWER)
            : this.selectionData.selectedTopology;
        let adc = this.selectionData.selectedAdc;
        let driver = this.selectionData.selectedDriver;

        let inData = {
            /**
             * Primary information
             * Used in the naming of LTSpice files
             */
            // The adc is used in the download file name, can't contain a pipe
            ADCname: this.selectionData.selectedAdc.part.replace('|', '-'),
            ADC: this.selectionData.selectedAdc.partCore,
            Drivername: this.selectionData.selectedDriver.part.replace('|', '-'),
            topology: LtSpiceConsts.TOPOLOGY[topology.id],

            title: intl.t('title'),

            ampType:
                this.selectionData.selectedDriver.type === Consts.DRIVER_TYPE.AMP
                    ? LtSpiceConsts.DRIVER_TYPE.AMP
                    : LtSpiceConsts.DRIVER_TYPE.DIFF_AMP,
            adcType: adc.type === Consts.ADC_TYPE.SE ? LtSpiceConsts.ADC_TYPE.SE : LtSpiceConsts.ADC_TYPE.DIFF,
            sourceType:
                this.selectionData.selectedSourceType.id === Consts.SOURCE_TYPE.SE
                    ? LtSpiceConsts.SOURCE_TYPE.SE
                    : LtSpiceConsts.SOURCE_TYPE.DIFF,

            currentUrl: top.location.href,
            baseUrl: this.urlData.adcDriverUrl,

            /**
             * Flags to help determine LTSpice template
             */
            isADCInLTSpice: adc.inLTSpice,
            isFFTLTSpice: adc.fftLTSpice,
            hasHiZ: adc.hasVariant && !adc.variants && adc.variant.code === Consts.VARIANT.ADC.HIZ,
            isMultiplexed: this.model.multiplexed,
            ADCSchematicTemplate: adc.schematicTemplate,

            /**
             * ADC Fields
             */
            // with values like bits, only replace the number, not the whole line.
            bits: PrettyNum.spice(adc.bits),
            cadc: PrettyNum.spice(adc.cadc),
            radc: adc.radc, // switch this when it becomes available
            maxSampleRate: PrettyNum.spice(adc.maxSampleRate),
            acquisitionTimeMin: PrettyNum.spice(adc.acquisitionTimeMin),
            kickbackVrefMultiplier: PrettyNum.spice(adc.kickbackVrefMultiplier),
            kickbackType: adc.kickbackType,
            HiZ: adc.hasCurrentVariant && adc.currentVariant.code === Consts.VARIANT.ADC.HIZ ? 5 : 0,

            /**
             * Driver Fields
             */
            ro: PrettyNum.spice(driver.Ro),
            aol: '100k', // switch this when it becomes available
            gbw: PrettyNum.spice(driver.unityGain),
            sr: PrettyNum.spice(driver.slewRate),
            ilimit: PrettyNum.spice(driver.IoutLimit),
            rail: driver.VoutHdrmPos,
            ioutSC: PrettyNum.spice(driver.IoutSC),
            vn: PrettyNum.spice(driver.vn),
            inp: PrettyNum.spice(driver.inp),

            /**
             * Input Fields
             */
            conversionTime: PrettyNum.spice(this.model.inputSettlingArrays.conversionTime),
            vref: PrettyNum.spice(this.model.vref, 4),

            cext: PrettyNum.spice(
                ['AD4080', 'AD4081', 'AD4084'].includes(adc.partCore) ? this.model.cext * 2 : this.model.cext,
            ), // Double Cext for AD4080, 81, 84
            rext: PrettyNum.spice(this.model.rext),
            rf: PrettyNum.spice(this.model.rf),
            rg: PrettyNum.spice(this.model.rg),
            cf: PrettyNum.spice(this.model.cf),
            sampleRate: PrettyNum.spice(this.model.samplingRate),
            gain: PrettyNum.spice(this.model.gain),
            frequency: frequency,

            VinDc: PrettyNum.spice(this.model.inputSettlingArrays.vinDcInitial),
            vcm: PrettyNum.spice(this.model.inputSettlingArrays.vinCmInitial),

            Vsp: this.model.vSupplyPos,
            Vsm: this.model.vSupplyNeg,

            vdiff: vdiff,

            opRangeTime: PrettyNum.spice((1 / this.model.frequency) * 5),

            /**
             * Computed Strings
             */
            kickbackModel: this.getKickbackModel('', '-'),
            kickbackModelP: this.getKickbackModel('+', '-'),
            kickbackModelN: this.getKickbackModel('-', '+'),

            kick: this.getKick(),
            pulseOp: vdiff,
            opRangeVdiff: `SINE(0 ${vdiff} ${frequency})`,
            thdFFTVdiff: `SINE(0 {vmag} {fin})`,
            transientVdiff: topology.id === Consts.TOPOLOGY.INVERTING ? '-' + vdiff : vdiff,

            /**
             * Calculated values
             */
            N: '2**14',
            M: utils.closestPrime((this.model.frequency / this.model.samplingRate) * /* n: */ Math.pow(2, 14)),
        };

        console.log('To spice: ', inData);

        return inData;
    }

    getKick() {
        let selectedAdc = this.selectionData.selectedAdc;
        let baseSelectedAdc = this.selectionData.baseSelectedAdc;
        let hiZSelectedAdc = this.selectionData.hiZSelectedAdc;

        let baseKickbackMultipler = baseSelectedAdc.kickbackVrefMultiplier;
        let kickbackMultiplier = selectedAdc.kickbackVrefMultiplier;

        return hiZSelectedAdc
            ? `if(HiZ, ${hiZSelectedAdc.kickbackVrefMultiplier}, ${baseKickbackMultipler})`
            : kickbackMultiplier;
    }

    getKickbackModel(suffix, operator) {
        let replacers = {
            suffix: '${suffix}',
            operator: '${operator}',
            kick: '${kick}',
        };

        let selectedAdc = this.selectionData.selectedAdc;

        let fixedKickbackModel = 'V=0';
        //let kickbackModelFormula = `V=V(prevValue${replacers.suffix}) ${replacers.operator} ({vref}*{${replacers.kick}})`;
        let kickbackModelFormula = `V=V(prevValue${replacers.suffix}) ${replacers.operator} ({vref}*{kick})`;
        let kickbackType = selectedAdc.kickbackType;

        return !(kickbackType == Consts.KICKBACK_TYPE.FIXED)
            ? kickbackModelFormula.replace(replacers.suffix, suffix).replace(replacers.operator, operator)
            : //.replace(replacers.kick, kick) :
              fixedKickbackModel;
    }

    /**
     * The following functions are all related to the lt spice template creation and the
     * download of the zip file.
     */
    downloadLtSpiceData(downloadData, zipFileName) {
        waitForPromise(
            fetch(this.urlData.apiUrl, {
                headers: {
                    Accept: 'application/zip',
                    'Content-Type': 'application/json',
                },
                method: 'POST',
                body: '"' + encodeURIComponent(JSON.stringify(downloadData)) + '"',
            })
                .then((response) => {
                    if (response.ok) {
                        let contentType = response.headers.get('Content-Type');

                        return Promise.all([contentType, zipFileName, response.blob()]);
                    } else {
                        console.error(`LtSpice Download Error: ${response.statusText.toString()}`);
                        return Promise.all(['', '', null]);
                    }
                })
                .then(([contentType, fileName, result]) => {
                    if (result) {
                        utils.downloadFile(contentType, fileName, result);
                    }
                })
                .catch((response) => {
                    console.error(`LtSpice Error: ${response.toString()}`);
                }),
        );
    }

    async downloadSimulation() {
        let data = this.generateLtSpiceData();
        let fileData = await this.generateFileData(data);

        let downloadData = {
            ToolId: Consts.API_TOOL_ID,
            ToolName: 'SAR',
            UploadFiles: fileData,
        };

        this.downloadLtSpiceData(
            downloadData,
            `LTspice simulation files for ${data.ADCname} and ${data.Drivername}.zip`,
        );
    }

    /**
     * Get the ltspice file mappings (from ltspicedata.js).
     * Retrieve the initial template(s) for each mapping, create the combined template (if there is more than one initial template).
     * Make any text replacements / overrides to the combined template, template names, and file names.
     * NOTE: replacements are noted in the templates within brackets: <<>> and overrides are not.
     *       Replacements can be specified for any file, or can be specified on the mapping level
     *       Overrides can be specified for any file or can be specified on the template level
     * Return the list of files (generated from the templates)
     */
    async generateFileData(data) {
        let mappings = cloneDeep(LtSpiceData.templateToFilesMappings);
        let fileData = [];

        for (let mapping of mappings) {
            let fileItem = await this.processMapping(mapping, data);
            if (fileItem) fileData.push(fileItem);
        }

        return fileData;
    }

    async processMapping(mapping, data) {
        {
            if (mapping.conditional && !mapping.conditional(data)) {
                return null;
            }

            for (let templateItem of mapping.in) {
                await this.getTemplate(templateItem, mapping, data);
            }

            // Make template level overrides
            let value = this.makeOverrides(mapping.finalTemplate, LtSpiceConsts.TEMPLATE_OVERRIDES, data);

            // Make mapping level replacements
            if ('replacements' in mapping) {
                value = this.makeMappingReplacements(value, mapping.replacements, data);
            }

            value = this.makeReplacements(value, data, true);

            return {
                name: `${this.makeReplacements(mapping.out, data, true)}.${mapping.fileType}`,
                value: value, // the final value to be stored in the file
                location: '', // folder in zip - empty string should work for base directory
                type: 'LTSPICE',
            };
        }
    }

    getTemplate(templateItem, mapping, data) {
        let templateName = utils.isFunction(templateItem.name) ? templateItem.name(data) : templateItem.name;
        templateName = this.makeReplacements(templateName, data, true);

        return new Promise((resolve) => {
            let url = `${utils.getLtSpiceLocation()}/${templateName}.${mapping.fileType}`;
            waitForPromise(
                fetch(url)
                    .then((response) => response.arrayBuffer())
                    // Convert to ISO-8859-1
                    .then((arrayBuffer) => String.fromCharCode.apply(null, new Uint8Array(arrayBuffer)))
                    .then((result) => {
                        this.prepareTemplateItem(mapping, templateItem, result, data);
                        resolve();
                    })
                    .catch((error) => {
                        console.error(`LTSpice file: ${url} could not be loaded: ${error}`);
                    }),
            );
        });
    }

    prepareTemplateItem(mapping, templateItem, initialTemplate, data) {
        // Remove any empty lines and turn into an array of lines
        let templateArray = initialTemplate.trim().split('\n');

        if (templateItem.numRemoveTopLines) {
            let x = utils.isFunction(templateItem.numRemoveTopLines)
                ? templateItem.numRemoveTopLines(data)
                : templateItem.numRemoveTopLines;
            while (x > 0) {
                templateArray.shift();
                x = x - 1;
            }
        }

        if (templateItem.numRemoveBottomLines) {
            let x = utils.isFunction(templateItem.numRemoveBottomLines)
                ? templateItem.numRemoveBottomLines(data)
                : templateItem.numRemoveBottomLines;
            while (x > 0) {
                templateArray.pop();
                x = x - 1;
            }
        }

        let template = templateArray.join('\n');

        if ('overrides' in templateItem) {
            template = this.makeOverrides(template, templateItem.overrides);
        }

        if (mapping.finalTemplate) mapping.finalTemplate = mapping.finalTemplate.concat('\n');
        else mapping.finalTemplate = '';

        mapping.finalTemplate = mapping.finalTemplate.concat(template);
    }

    makeMappingReplacements(baseString, mappingReplacements, data) {
        let replacements = {};
        if (!mappingReplacements) return baseString;

        Object.keys(mappingReplacements).forEach((key) => {
            let replacementFunction = mappingReplacements[key],
                replacementValue = replacementFunction(data);

            replacements[key] = replacementValue;
        });

        return this.makeReplacements(baseString, replacements);
    }

    makeOverrides(baseString, overrides) {
        if (!overrides) return baseString;

        Object.keys(overrides).forEach((key) => {
            let override = overrides[key];
            baseString = baseString.replaceAll(key, override);
        });

        return baseString;
    }

    makeReplacements(baseString, replacements, replacementsShouldExist = false) {
        let regex = /<<(\w+)?>>/g;
        let matches = baseString.match(regex);
        if (!matches) return baseString;

        matches.removeDuplicates().forEach((match) => {
            match = match.replace('<<', '');
            match = match.replace('>>', '');

            if (match in replacements) {
                baseString = baseString.replaceAll(`<<${match}>>`, replacements[match]);
            } else if (replacementsShouldExist) {
                console.error(`LtSpice: ${match} not found in the replacement options`);
            }
        });

        return baseString;
    }
}
